import {
  type CurrentUser,
  type LoginResponse,
  type SessionAccount,
  accountsResponseSchema,
  currentUserSchema,
  loginResponseSchema,
} from '@xfile/shared';
import { SLOT_HEADER } from '../lib/activeSlot';
import { withTimeoutSignal } from '../lib/timeout';
import { nowMs, requestIdOf, traceApi } from '../diagnostics/trace';

/**
 * `fetch` tracé (métadonnées HTTP) pour les appels d'authentification, BORNÉ dans
 * le temps. Sans garde-temps, un serveur muet fige le bootstrap sur son écran de
 * chargement — l'app paraît morte alors que la session est valide.
 */
async function tracedFetch(path: string, init: RequestInit): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const signal = withTimeoutSignal(init.signal);
  const started = nowMs();
  try {
    const res = await fetch(path, { ...init, ...(signal ? { signal } : {}) });
    traceApi(method, path, res.status, Math.round(nowMs() - started), requestIdOf(res));
    return res;
  } catch (err) {
    traceApi(method, path, 0, Math.round(nowMs() - started), null);
    throw err;
  }
}

/**
 * Client typé de l'API d'authentification multi-login (XFILE-14). Consomme les
 * contrats partagés `@xfile/shared` — les DTO ne sont jamais redéfinis ici.
 *
 * Le cookie de session (faisceau chiffré) est same-origin. Le compte visé est
 * désigné par l'en-tête `X-Active-Slot` : `me`/`logout` l'envoient EXPLICITEMENT
 * (on peut cibler un slot non actif). `login`/`accounts` n'en ont pas besoin
 * (login crée un slot ; accounts lit tout le faisceau).
 */

/** Erreur d'appel API : code normalisé + statut HTTP. */
export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'AuthApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON : code générique.
  }
  return 'UNKNOWN';
}

/**
 * `GET /api/auth/me` pour un slot donné — utilisateur du slot, ou `null` si le
 * slot n'a pas de session valide (401). Les autres statuts lèvent (backend en
 * erreur → l'UI ne déconnecte PAS, elle signale un problème réseau).
 */
export async function fetchMe(slot: number, signal?: AbortSignal): Promise<CurrentUser | null> {
  const res = await tracedFetch('/api/auth/me', {
    credentials: 'same-origin',
    headers: { [SLOT_HEADER]: String(slot) },
    signal: signal ?? null,
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new AuthApiError(res.status, await errorCodeOf(res));
  return currentUserSchema.parse(await res.json());
}

/**
 * `POST /api/auth/login` — empile un compte ; renvoie `{ slot, user }`. Lève sur 401/409.
 *
 * `stayLoggedIn` pilote la durée du cookie côté serveur : persistant (survit à la
 * fermeture du navigateur) si `true`, cookie de session (effacé à la fermeture) sinon.
 */
export async function login(
  email: string,
  password: string,
  stayLoggedIn: boolean,
): Promise<LoginResponse> {
  const res = await tracedFetch('/api/auth/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, stayLoggedIn }),
  });
  if (!res.ok) throw new AuthApiError(res.status, await errorCodeOf(res));
  return loginResponseSchema.parse(await res.json());
}

/**
 * `POST /api/auth/logout` — déconnecte le compte du `slot` visé (via `X-Active-Slot`),
 * ou TOUS les comptes si `all`. Best-effort : n'échoue jamais côté UI.
 */
export async function logout(slot: number, all = false): Promise<void> {
  await tracedFetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', [SLOT_HEADER]: String(slot) },
    body: JSON.stringify({ all }),
  });
}

/** `GET /api/auth/accounts` — comptes actuellement empilés dans ce navigateur. */
export async function fetchAccounts(signal?: AbortSignal): Promise<SessionAccount[]> {
  const res = await tracedFetch('/api/auth/accounts', {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  if (!res.ok) throw new AuthApiError(res.status, await errorCodeOf(res));
  return accountsResponseSchema.parse(await res.json()).accounts;
}
